import {
  CandidateSourceId,
  FormulaNode,
  RECOMMENDATION_ENGINE_DIVERSITY_DIMENSIONS,
  RecommendationEngineCoOccurrenceSourceParams,
  RecommendationEngineCommonSourceParams,
  RecommendationEngineEndpoint,
  RecommendationEngineEndpointConfig,
  RecommendationEngineFilterConfig,
  RecommendationEngineImageAnnSourceParams,
  RecommendationEngineInterestSourceParams,
  RecommendationEngineNearbySourceParams,
  RecommendationEnginePromotionTagsSourceParams,
  RecommendationEngineRerankRule,
  RecommendationEngineSourceConfig,
  RecommendationEngineTrendingSourceParams,
} from "../../types/recommendationEngine";

// Must match the backend's MAX_SOURCE_RADIUS_KM (applies to both per-source
// params.radiusKm and signalParams.*.radiusKm) or saves return 400.
export const RADIUS_KM_MAX = 100;

export const isValidRadiusKm = (value: unknown): value is number =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  value > 0 &&
  value <= RADIUS_KM_MAX;

/**
 * True when a source radius or the distance-decay radius is set but out of
 * range; the server rejects such a pipeline.
 */
export const hasInvalidPipelineRadius = (
  pipeline: RecommendationEngineEndpointConfig,
): boolean => {
  const decayRadius = pipeline.signalParams?.distance_decay?.radiusKm;
  return (
    pipeline.candidateSources.some((source) => {
      const value = source.params?.radiusKm;
      return value !== undefined && !isValidRadiusKm(value);
    }) ||
    (decayRadius !== undefined && !isValidRadiusKm(decayRadius))
  );
};

export const RECOMMENDATION_ENGINE_CONFIG_KEYS = {
  similarPipeline: "flyer_item.recommendation_engine.similar_pipeline",
  recommendedPipeline: "flyer_item.recommendation_engine.recommended_pipeline",
  nearbyPicksPipeline: "flyer_item.recommendation_engine.nearby_picks_pipeline",
  trendingNowPipeline: "flyer_item.recommendation_engine.trending_now_pipeline",
  browseByInterestPipeline:
    "flyer_item.recommendation_engine.browse_by_interest_pipeline",
  smartPicksPipeline: "flyer_item.recommendation_engine.smart_picks_pipeline",
  literalAbsMax: "flyer_item.recommendation_engine.literal_abs_max",
} as const;

export const RECOMMENDATION_ENGINE_ENDPOINT_ORDER: RecommendationEngineEndpoint[] =
  [
    "recommended",
    "similar",
    "nearby_picks",
    "trending_now",
    "browse_by_interest",
    "smart_picks",
  ];

export const RECOMMENDATION_ENGINE_ENDPOINT_LABELS: Record<
  RecommendationEngineEndpoint,
  string
> = {
  recommended: "Recommended configuration",
  similar: "Similar configuration",
  nearby_picks: "Nearby Picks configuration",
  trending_now: "Trending Now configuration",
  browse_by_interest: "Browse by Interest configuration",
  smart_picks: "Smart Picks configuration",
};

export const RECOMMENDATION_ENGINE_ENDPOINT_CONFIG_KEYS: Record<
  RecommendationEngineEndpoint,
  string
> = {
  similar: RECOMMENDATION_ENGINE_CONFIG_KEYS.similarPipeline,
  recommended: RECOMMENDATION_ENGINE_CONFIG_KEYS.recommendedPipeline,
  nearby_picks: RECOMMENDATION_ENGINE_CONFIG_KEYS.nearbyPicksPipeline,
  trending_now: RECOMMENDATION_ENGINE_CONFIG_KEYS.trendingNowPipeline,
  browse_by_interest:
    RECOMMENDATION_ENGINE_CONFIG_KEYS.browseByInterestPipeline,
  smart_picks: RECOMMENDATION_ENGINE_CONFIG_KEYS.smartPicksPipeline,
};

export const CANDIDATE_SOURCE_LABELS: Record<CandidateSourceId, string> = {
  exact_name: "Exact normalized name",
  image_ann: "Image similarity",
  es_fuzzy: "Fuzzy name discovery",
  shopping_list_cooccurrence: "Bought together behavior",
  category_popular: "Popular in same category",
  global_popular: "Popular overall",
  nearby_active: "Active nearby items",
  trending_items: "Trending items",
  interest_category: "Interest categories",
  interest_promotion_tag: "Interest promotion tags",
  favorite_store: "Favorite stores",
  source_category_match: "Source category match",
  source_promotion_tag_match: "Source promotion tag match",
  source_merchant_match: "Source merchant match",
  source_flyer_list_match: "Same flyer list",
  popular_on_shopping_lists: "Popular on shopping lists",
  geo_popular_on_shopping_lists: "Popular on shopping lists nearby",
  interest_category_popular: "Popular in interest categories",
  promotion_tags: "Promotion tags",
};

const ENDPOINT_SOURCE_ORDER: Record<
  RecommendationEngineEndpoint,
  CandidateSourceId[]
> = {
  similar: [
    "exact_name",
    "image_ann",
    "es_fuzzy",
    "source_category_match",
    "source_promotion_tag_match",
    "nearby_active",
    "geo_popular_on_shopping_lists",
  ],
  recommended: [
    "exact_name",
    "image_ann",
    "es_fuzzy",
    "shopping_list_cooccurrence",
    "category_popular",
    "global_popular",
    "source_category_match",
    "source_promotion_tag_match",
    "source_merchant_match",
    "source_flyer_list_match",
    "nearby_active",
    "geo_popular_on_shopping_lists",
  ],
  nearby_picks: [
    "nearby_active",
    "trending_items",
    "geo_popular_on_shopping_lists",
    "popular_on_shopping_lists",
  ],
  trending_now: [
    "trending_items",
    "nearby_active",
    "geo_popular_on_shopping_lists",
    "popular_on_shopping_lists",
  ],
  browse_by_interest: [
    "interest_category",
    "interest_promotion_tag",
    "favorite_store",
    "interest_category_popular",
    "trending_items",
    "nearby_active",
    "geo_popular_on_shopping_lists",
    "popular_on_shopping_lists",
  ],
  smart_picks: [
    "nearby_active",
    "trending_items",
    "interest_category",
    "interest_promotion_tag",
    "favorite_store",
    "interest_category_popular",
    "geo_popular_on_shopping_lists",
    "popular_on_shopping_lists",
  ],
};

const buildSourceConfig = (
  sourceId: CandidateSourceId,
  enabled: boolean,
): RecommendationEngineSourceConfig => {
  switch (sourceId) {
    case "exact_name":
      return { sourceId, enabled, cap: 500 };
    case "image_ann":
      return { sourceId, enabled, cap: 200, params: {} };
    case "es_fuzzy":
      return { sourceId, enabled, cap: 100 };
    case "shopping_list_cooccurrence":
      return {
        sourceId,
        enabled,
        cap: 60,
        params: {
          useExactNameAnchors: true,
          useImageAnchors: true,
          useEsFuzzyAnchors: true,
        },
      };
    case "category_popular":
      return { sourceId, enabled, cap: 60 };
    case "global_popular":
      return { sourceId, enabled, cap: 60 };
    case "nearby_active":
      return {
        sourceId,
        enabled,
        cap: 200,
        params: { radiusKm: 12 },
      };
    case "trending_items":
      return {
        sourceId,
        enabled,
        cap: 200,
        params: { hoursWindow: 24 },
      };
    case "interest_category":
      return { sourceId, enabled, cap: 100, params: {} };
    case "interest_promotion_tag":
      return { sourceId, enabled, cap: 100, params: {} };
    case "favorite_store":
      return { sourceId, enabled, cap: 100, params: {} };
    case "source_category_match":
      return { sourceId, enabled, cap: 100 };
    case "source_promotion_tag_match":
      return { sourceId, enabled, cap: 100 };
    case "source_merchant_match":
      return { sourceId, enabled, cap: 100 };
    case "source_flyer_list_match":
      return { sourceId, enabled, cap: 100 };
    case "popular_on_shopping_lists":
      return { sourceId, enabled, cap: 100 };
    case "geo_popular_on_shopping_lists":
      return {
        sourceId,
        enabled,
        cap: 100,
        params: { radiusKm: 12 },
      };
    case "interest_category_popular":
      return { sourceId, enabled, cap: 100 };
    case "promotion_tags":
      return { sourceId, enabled, cap: 150, params: { tagIds: [] } };
    default:
      return { sourceId, enabled, cap: 50 };
  }
};

export const getAllowedSources = (
  endpoint: RecommendationEngineEndpoint,
): CandidateSourceId[] => ENDPOINT_SOURCE_ORDER[endpoint];

type WeightedTerm = {
  value: number;
  signalId?: string;
  node?: FormulaNode;
};

const literalNode = (value: number): FormulaNode => ({
  type: "literal" as const,
  value,
});

const signalNode = (signalId: string): FormulaNode => ({
  type: "signal" as const,
  signalId,
});

const divideNode = (left: FormulaNode, right: FormulaNode): FormulaNode => ({
  type: "binary" as const,
  op: "/" as const,
  left,
  right,
});

const createWeightedSignalFormula = (terms: WeightedTerm[]): FormulaNode => {
  if (terms.length === 0) {
    return literalNode(0);
  }

  const buildTerm = (term: WeightedTerm): FormulaNode => ({
    type: "binary" as const,
    op: "*" as const,
    left: literalNode(term.value),
    right: term.node ?? signalNode(term.signalId ?? ""),
  });

  return terms.slice(1).reduce<FormulaNode>(
    (acc, term) => ({
      type: "binary" as const,
      op: "+" as const,
      left: acc,
      right: buildTerm(term),
    }),
    buildTerm(terms[0]),
  );
};

const createDefaultFormula = (endpoint: RecommendationEngineEndpoint) => {
  switch (endpoint) {
    case "similar":
      return createWeightedSignalFormula([
        { value: 0.6, signalId: "image_similarity" },
        { value: 0.3, signalId: "name_similarity_fused" },
        { value: 1, signalId: "similar_match_type_boost" },
        { value: 0.1, signalId: "distance_decay" },
      ]);
    case "recommended":
      return createWeightedSignalFormula([
        { value: 0.4, signalId: "co_occurrence_freq" },
        { value: 0.18, signalId: "image_similarity" },
        { value: 0.14, signalId: "name_similarity" },
        { value: 0.11, signalId: "category_popularity" },
        { value: 0.07, signalId: "global_popularity" },
        { value: 0.1, signalId: "distance_decay" },
      ]);
    case "nearby_picks":
      return createWeightedSignalFormula([
        { value: 0.7, signalId: "distance_decay" },
        { value: 0.2, signalId: "on_shopping_list" },
        { value: 0.1, signalId: "within_radius" },
      ]);
    case "trending_now":
      return createWeightedSignalFormula([
        { value: 0.65, signalId: "shopping_list_velocity_24h" },
        { value: 0.2, signalId: "shopping_list_velocity_7d" },
        { value: 0.15, signalId: "on_shopping_list" },
      ]);
    case "browse_by_interest":
      return createWeightedSignalFormula([
        { value: 0.38, signalId: "interest_category_overlap" },
        { value: 0.3, signalId: "interest_promotion_tag_overlap" },
        { value: 0.17, signalId: "favorite_store_match" },
        { value: 0.1, signalId: "distance_decay" },
        { value: 0.05, signalId: "within_radius" },
      ]);
    case "smart_picks":
      return createWeightedSignalFormula([
        { value: 0.24, signalId: "distance_decay" },
        { value: 0.2, signalId: "interest_category_overlap" },
        { value: 0.16, signalId: "interest_promotion_tag_overlap" },
        { value: 0.12, signalId: "favorite_store_match" },
        { value: 0.1, signalId: "shopping_list_velocity_24h" },
        { value: 0.08, signalId: "list_freshness_decay" },
        {
          value: 0.08,
          node: divideNode(signalNode("discount_percent"), literalNode(100)),
        },
        { value: 0.05, signalId: "within_radius" },
      ]);
    default:
      return { type: "literal" as const, value: 0 };
  }
};

const createDefaultFilters = (
  endpoint: RecommendationEngineEndpoint,
): RecommendationEngineFilterConfig[] => {
  switch (endpoint) {
    case "similar":
      return [
        { kind: "min_signal", signalId: "similar_candidate_gate", min: 1 },
        { kind: "require_deals_page_eligible" },
      ];
    case "recommended":
      return [
        { kind: "require_on_shopping_list" },
        { kind: "require_deals_page_eligible" },
      ];
    case "nearby_picks":
      return [
        { kind: "require_deals_page_eligible" },
        { kind: "suppress_recently_served", lookbackHours: 24 },
      ];
    case "trending_now":
      return [
        { kind: "require_on_shopping_list" },
        { kind: "require_deals_page_eligible" },
        { kind: "max_flyer_list_age_days", maxAgeDays: 21 },
        { kind: "suppress_recently_served", lookbackHours: 24 },
      ];
    case "browse_by_interest":
      return [
        { kind: "require_deals_page_eligible" },
        { kind: "suppress_recently_served", lookbackHours: 24 },
      ];
    case "smart_picks":
      return [
        { kind: "require_deals_page_eligible" },
        { kind: "min_discount_percent", minPercent: 5 },
        { kind: "max_flyer_list_age_days", maxAgeDays: 14 },
        { kind: "suppress_recently_served", lookbackHours: 24 },
      ];
    default:
      return [];
  }
};

const createDefaultRerankRules = (
  endpoint: RecommendationEngineEndpoint,
): RecommendationEngineRerankRule[] => {
  switch (endpoint) {
    case "recommended":
      return [{ kind: "diversity_cap", dimension: "merchant", maxPerGroup: 3 }];
    case "nearby_picks":
    case "trending_now":
    case "browse_by_interest":
      return [{ kind: "diversity_cap", dimension: "merchant", maxPerGroup: 2 }];
    case "smart_picks":
      return [
        { kind: "diversity_cap", dimension: "merchant", maxPerGroup: 2 },
        { kind: "diversity_cap", dimension: "store", maxPerGroup: 3 },
      ];
    default:
      return [];
  }
};

export const createDefaultPipeline = (
  endpoint: RecommendationEngineEndpoint,
): RecommendationEngineEndpointConfig => {
  return {
    version: 1,
    displayLimit: 10,
    rankingWindowLimit: 100,
    excludeSourceMerchant: endpoint === "similar",
    postProcess: { clamp01: true, round4dp: true },
    candidateSources: getAllowedSources(endpoint).map((sourceId) =>
      buildSourceConfig(sourceId, true),
    ),
    filters: createDefaultFilters(endpoint),
    rerankRules: createDefaultRerankRules(endpoint),
    formula: createDefaultFormula(endpoint),
    // Every endpoint uses the distance_decay signal, so seed its decay radius.
    // Most surfaces default to 25 km; Nearby Picks is tighter at 12 km.
    signalParams: {
      distance_decay: {
        radiusKm: endpoint === "nearby_picks" ? 12 : 25,
      },
    },
  };
};

const parsePipelineValue = (raw: unknown): unknown => {
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const normalizeSourceConfig = (
  sourceId: CandidateSourceId,
  raw: Partial<RecommendationEngineSourceConfig> | undefined,
): RecommendationEngineSourceConfig => {
  const fallback = buildSourceConfig(sourceId, false);
  const rawCap = raw?.cap;
  const enabled =
    typeof raw?.enabled === "boolean" ? raw.enabled : fallback.enabled;
  const cap = isFiniteNumber(rawCap) ? rawCap : fallback.cap;

  const rawRadiusKm = (raw?.params as { radiusKm?: unknown } | undefined)
    ?.radiusKm;
  const radiusExtra: { params?: RecommendationEngineCommonSourceParams } =
    isFiniteNumber(rawRadiusKm) ? { params: { radiusKm: rawRadiusKm } } : {};

  if (sourceId === "exact_name") {
    return { sourceId, enabled, cap, ...radiusExtra };
  }

  if (sourceId === "image_ann") {
    const params =
      raw?.params && typeof raw.params === "object"
        ? (raw.params as RecommendationEngineImageAnnSourceParams)
        : {};
    return {
      sourceId,
      enabled,
      cap,
      params: {
        ...((fallback.params ??
          {}) as RecommendationEngineImageAnnSourceParams),
        ...params,
      },
    };
  }

  if (sourceId === "es_fuzzy") {
    return { sourceId, enabled, cap, ...radiusExtra };
  }

  if (sourceId === "shopping_list_cooccurrence") {
    const params =
      raw?.params && typeof raw.params === "object"
        ? (raw.params as RecommendationEngineCoOccurrenceSourceParams)
        : {};
    return {
      sourceId,
      enabled,
      cap,
      params: {
        ...((fallback.params ??
          {}) as RecommendationEngineCoOccurrenceSourceParams),
        ...params,
      },
    };
  }

  if (sourceId === "category_popular") {
    return { sourceId, enabled, cap, ...radiusExtra };
  }

  if (sourceId === "global_popular") {
    return { sourceId, enabled, cap, ...radiusExtra };
  }

  if (sourceId === "nearby_active") {
    const params =
      raw?.params && typeof raw.params === "object"
        ? (raw.params as RecommendationEngineNearbySourceParams)
        : {};
    return {
      sourceId,
      enabled,
      cap,
      params: {
        ...((fallback.params ?? {}) as RecommendationEngineNearbySourceParams),
        ...params,
      },
    };
  }

  if (sourceId === "trending_items") {
    const params =
      raw?.params && typeof raw.params === "object"
        ? (raw.params as RecommendationEngineTrendingSourceParams)
        : {};
    return {
      sourceId,
      enabled,
      cap,
      params: {
        ...((fallback.params ??
          {}) as RecommendationEngineTrendingSourceParams),
        ...params,
      },
    };
  }

  if (
    sourceId === "interest_category" ||
    sourceId === "interest_promotion_tag" ||
    sourceId === "favorite_store"
  ) {
    const params =
      raw?.params && typeof raw.params === "object"
        ? (raw.params as RecommendationEngineInterestSourceParams)
        : {};
    return {
      sourceId,
      enabled,
      cap,
      params: {
        ...((fallback.params ??
          {}) as RecommendationEngineInterestSourceParams),
        ...params,
      },
    };
  }

  if (
    sourceId === "source_category_match" ||
    sourceId === "source_promotion_tag_match" ||
    sourceId === "source_merchant_match" ||
    sourceId === "source_flyer_list_match"
  ) {
    return { sourceId, enabled, cap, ...radiusExtra };
  }

  if (
    sourceId === "popular_on_shopping_lists" ||
    sourceId === "interest_category_popular"
  ) {
    return { sourceId, enabled, cap, ...radiusExtra };
  }

  if (sourceId === "geo_popular_on_shopping_lists") {
    const params =
      raw?.params && typeof raw.params === "object"
        ? (raw.params as RecommendationEngineNearbySourceParams)
        : {};
    return {
      sourceId,
      enabled,
      cap,
      params: {
        ...((fallback.params ?? {}) as RecommendationEngineNearbySourceParams),
        ...params,
      },
    };
  }

  if (sourceId === "promotion_tags") {
    const params = (raw?.params ?? {}) as Partial<
      RecommendationEnginePromotionTagsSourceParams & { radiusKm: unknown }
    >;
    const tagIds = Array.isArray(params.tagIds)
      ? params.tagIds.filter(
          (tagId): tagId is string => typeof tagId === "string" && tagId !== "",
        )
      : [];
    return {
      sourceId,
      enabled,
      cap,
      params: {
        tagIds,
        ...(isFiniteNumber(params.radiusKm) ? { radiusKm: params.radiusKm } : {}),
      },
    };
  }

  return { sourceId: "favorite_store", enabled, cap, params: {} };
};

const normalizeFilters = (raw: unknown): RecommendationEngineFilterConfig[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  const filters: RecommendationEngineFilterConfig[] = [];

  raw.forEach((item) => {
    if (!item || typeof item !== "object") {
      return;
    }
    const filter = item as Partial<RecommendationEngineFilterConfig> & {
      minPercent?: unknown;
      maxAgeDays?: unknown;
      flyerItemIds?: unknown;
      lookbackHours?: unknown;
    };
    if (
      filter.kind === "min_signal" &&
      typeof filter.signalId === "string" &&
      isFiniteNumber(filter.min)
    ) {
      filters.push({
        kind: "min_signal",
        signalId: filter.signalId,
        min: filter.min,
      });
      return;
    }
    if (filter.kind === "require_on_shopping_list") {
      filters.push({ kind: "require_on_shopping_list" });
      return;
    }
    if (filter.kind === "exclude_source_merchant") {
      filters.push({ kind: "exclude_source_merchant" });
      return;
    }
    if (
      filter.kind === "min_discount_percent" &&
      isFiniteNumber(filter.minPercent)
    ) {
      filters.push({
        kind: "min_discount_percent",
        minPercent: filter.minPercent,
      });
      return;
    }
    if (
      filter.kind === "max_flyer_list_age_days" &&
      isFiniteNumber(filter.maxAgeDays)
    ) {
      filters.push({
        kind: "max_flyer_list_age_days",
        maxAgeDays: filter.maxAgeDays,
      });
      return;
    }
    if (
      filter.kind === "exclude_flyer_item_ids" &&
      Array.isArray(filter.flyerItemIds)
    ) {
      const ids = filter.flyerItemIds.filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      );
      filters.push({ kind: "exclude_flyer_item_ids", flyerItemIds: ids });
      return;
    }
    if (filter.kind === "require_deals_page_eligible") {
      filters.push({ kind: "require_deals_page_eligible" });
      return;
    }
    if (
      filter.kind === "suppress_recently_served" &&
      isFiniteNumber(filter.lookbackHours)
    ) {
      filters.push({
        kind: "suppress_recently_served",
        lookbackHours: filter.lookbackHours,
      });
    }
  });

  return filters;
};

const isDiversityDimension = (
  value: unknown,
): value is (typeof RECOMMENDATION_ENGINE_DIVERSITY_DIMENSIONS)[number] =>
  typeof value === "string" &&
  (RECOMMENDATION_ENGINE_DIVERSITY_DIMENSIONS as readonly string[]).includes(
    value,
  );

const normalizeRerankRules = (
  raw: unknown,
): RecommendationEngineRerankRule[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  const rules: RecommendationEngineRerankRule[] = [];

  raw.forEach((item) => {
    if (!item || typeof item !== "object") {
      return;
    }
    const rule = item as Partial<RecommendationEngineRerankRule> & {
      dimension?: unknown;
      maxPerGroup?: unknown;
      flyerItemIds?: unknown;
      multiplier?: unknown;
    };
    if (
      rule.kind === "diversity_cap" &&
      isDiversityDimension(rule.dimension) &&
      isFiniteNumber(rule.maxPerGroup)
    ) {
      rules.push({
        kind: "diversity_cap",
        dimension: rule.dimension,
        maxPerGroup: rule.maxPerGroup,
      });
      return;
    }
    if (
      (rule.kind === "boost_flyer_item_ids" ||
        rule.kind === "demote_flyer_item_ids") &&
      Array.isArray(rule.flyerItemIds) &&
      isFiniteNumber(rule.multiplier)
    ) {
      const ids = rule.flyerItemIds.filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      );
      rules.push({
        kind: rule.kind,
        flyerItemIds: ids,
        multiplier: rule.multiplier,
      });
    }
  });

  return rules;
};

const normalizeSignalParams = (
  raw: unknown,
): Record<string, { radiusKm?: number }> | undefined => {
  if (!raw || typeof raw !== "object") {
    return undefined;
  }
  const result: Record<string, { radiusKm?: number }> = {};
  Object.entries(raw as Record<string, unknown>).forEach(
    ([signalId, value]) => {
      if (!value || typeof value !== "object") {
        return;
      }
      // Preserve the full entry (don't strip unknown fields); only ensure a
      // present radiusKm is a finite number.
      const entry = value as { radiusKm?: unknown } & Record<string, unknown>;
      result[signalId] = {
        ...entry,
        ...(isFiniteNumber(entry.radiusKm) ? { radiusKm: entry.radiusKm } : {}),
      } as { radiusKm?: number };
    },
  );
  return Object.keys(result).length > 0 ? result : undefined;
};

export const normalizeRecommendationEnginePipeline = (
  raw: unknown,
  endpoint: RecommendationEngineEndpoint,
): RecommendationEngineEndpointConfig => {
  const defaults = createDefaultPipeline(endpoint);
  const parsed = parsePipelineValue(raw);
  if (!parsed || typeof parsed !== "object") {
    return defaults;
  }

  const pipeline = parsed as Partial<RecommendationEngineEndpointConfig>;
  const rawSources = Array.isArray(pipeline.candidateSources)
    ? pipeline.candidateSources
    : [];
  const allowedSources = getAllowedSources(endpoint);
  const allowedSet = new Set<CandidateSourceId>(allowedSources);
  const sourceMap = new Map<
    CandidateSourceId,
    Partial<RecommendationEngineSourceConfig>
  >();
  // Sources present in the saved config but not in this endpoint's hardcoded
  // order — keep them (in saved order, after the known ones) instead of
  // silently dropping their config on a load/save round-trip.
  const extraSources: CandidateSourceId[] = [];
  rawSources.forEach((item) => {
    if (!item || typeof item !== "object") {
      return;
    }
    const source = item as Partial<RecommendationEngineSourceConfig>;
    if (typeof source.sourceId !== "string") {
      return;
    }
    const sourceId = source.sourceId as CandidateSourceId;
    const isKnownSource = Boolean(CANDIDATE_SOURCE_LABELS[sourceId]);
    if (!isKnownSource) {
      return;
    }
    sourceMap.set(sourceId, source);
    if (!allowedSet.has(sourceId) && !extraSources.includes(sourceId)) {
      extraSources.push(sourceId);
    }
  });
  const candidateSources = [...allowedSources, ...extraSources].map((sourceId) =>
    normalizeSourceConfig(sourceId, sourceMap.get(sourceId)),
  );

  const signalParams = normalizeSignalParams(pipeline.signalParams);

  return {
    version: isFiniteNumber(pipeline.version)
      ? pipeline.version
      : defaults.version,
    displayLimit: isFiniteNumber(pipeline.displayLimit)
      ? pipeline.displayLimit
      : defaults.displayLimit,
    rankingWindowLimit: isFiniteNumber(pipeline.rankingWindowLimit)
      ? pipeline.rankingWindowLimit
      : defaults.rankingWindowLimit,
    excludeSourceMerchant:
      typeof pipeline.excludeSourceMerchant === "boolean"
        ? pipeline.excludeSourceMerchant
        : defaults.excludeSourceMerchant,
    postProcess: {
      clamp01: pipeline.postProcess?.clamp01 ?? defaults.postProcess.clamp01,
      round4dp: pipeline.postProcess?.round4dp ?? defaults.postProcess.round4dp,
    },
    candidateSources,
    filters: normalizeFilters(pipeline.filters),
    rerankRules: normalizeRerankRules(
      (pipeline as { rerankRules?: unknown }).rerankRules,
    ),
    formula: pipeline.formula ?? defaults.formula,
    ...(signalParams ? { signalParams } : {}),
  };
};
